import { useEffect, useRef, useState } from 'react'
import { useParams, useSearchParams } from 'react-router'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import {
  ChevronUp,
  Maximize,
  Minimize,
  Monitor,
  RotateCw,
  Smartphone,
  type LucideIcon,
} from 'lucide-react'

import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { CreativePreviewEventPanel } from '@/components/CreativePreviewEventPanel'
import { CreativeLivePreviewForm } from '@/components/CreativeLivePreviewForm'
import { AuthService } from '@/services/core/auth'
import { CreativesApi } from '@/services/creatives/creativesApi'
import {
  MOBILE_VIEWPORT,
  isPreviewEventMessage,
  type PreviewDevice,
  type PreviewEvent,
} from '@/services/creatives/previewEvents'

/** Where the creative is previewed: on a blank page here, or on a real site through a live link. */
type PreviewWebsite = 'blank' | 'live'

function ToolbarButton({
  label,
  onClick,
  children,
}: {
  label: string
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button type="button" variant="ghost" size="icon" onClick={onClick} aria-label={label}>
          {children}
        </Button>
      </TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  )
}

const DEVICES: Array<{ value: PreviewDevice; icon: LucideIcon; labelKey: string }> = [
  { value: 'desktop', icon: Monitor, labelKey: 'creatives.previewDesktop' },
  { value: 'mobile', icon: Smartphone, labelKey: 'creatives.previewMobile' },
]

/**
 * A saved creative in a preview window of its own: the creative on a blank
 * page as a desktop or a phone shows it, above the events it fires. The
 * creative renders in a frame sized to the device, so layouts that size
 * themselves to the viewport behave as they would on that device. Signed-in
 * users can also build a live link that shows it on a real site.
 */
export function CreativeBlankPreviewPage() {
  const { creativeId } = useParams<{ creativeId: string }>()
  const [params] = useSearchParams()
  const { t } = useTranslation()
  const frame = useRef<HTMLIFrameElement>(null)

  const [device, setDevice] = useState<PreviewDevice>(
    params.get('device') === 'mobile' ? 'mobile' : 'desktop'
  )
  const [events, setEvents] = useState<PreviewEvent[]>([])
  const [loadCount, setLoadCount] = useState(0)
  const [panelOpen, setPanelOpen] = useState(false)
  const [website, setWebsite] = useState<PreviewWebsite>('blank')
  const [fullScreen, setFullScreen] = useState(false)

  // The live link needs the creative itself, which only a signed-in user can load
  const signedIn = AuthService.isAuthenticated()
  const { data: creative } = useQuery({
    queryKey: ['creative', creativeId],
    queryFn: () => CreativesApi.getById(creativeId!),
    enabled: signedIn && !!creativeId,
    staleTime: 5 * 60 * 1000,
  })

  const name = params.get('name') ?? creative?.name
  useEffect(() => {
    document.title = name
      ? t('creatives.previewWindowTitleNamed', { name })
      : t('creatives.previewBlankPageTitle')
  }, [name, t])

  useEffect(() => {
    const onMessage = (e: MessageEvent) => {
      if (e.origin !== window.location.origin || e.source !== frame.current?.contentWindow) return
      if (isPreviewEventMessage(e.data)) setEvents((current) => [...current, e.data.event])
    }
    window.addEventListener('message', onMessage)
    return () => window.removeEventListener('message', onMessage)
  }, [])

  useEffect(() => {
    const onChange = () => setFullScreen(!!document.fullscreenElement)
    document.addEventListener('fullscreenchange', onChange)
    return () => document.removeEventListener('fullscreenchange', onChange)
  }, [])

  // Each load starts the creative afresh, so its events start afresh too
  const reload = () => {
    setEvents([])
    setLoadCount((count) => count + 1)
  }

  const changeDevice = (value: PreviewDevice) => {
    setDevice(value)
    reload()
  }

  // The blank page renders the creative again on the way back, so it gets fresh events
  const changeWebsite = (value: PreviewWebsite) => {
    setWebsite(value)
    if (value === 'blank') reload()
  }

  const toggleFullScreen = () => {
    if (document.fullscreenElement) void document.exitFullscreen()
    else void document.documentElement.requestFullscreen()
  }

  const frameParams = new URLSearchParams(params)
  frameParams.delete('name')
  frameParams.delete('device')
  const frameSrc = `/preview-creative/${encodeURIComponent(creativeId ?? '')}/frame?${frameParams}`

  const showLive = website === 'live' && !!creative

  return (
    <div className="flex h-screen flex-col bg-background text-foreground">
      <header className="flex items-end gap-6 border-b bg-card px-4 py-2">
        {creative && (
          <div className="flex flex-col gap-1">
            <Label htmlFor="preview-website" className="text-xs text-(--ad-text-muted)">
              {t('creatives.previewWebsite')}
            </Label>
            <Select value={website} onValueChange={(v) => changeWebsite(v as PreviewWebsite)}>
              <SelectTrigger id="preview-website" className="w-40">
                <SelectValue>
                  {website === 'live'
                    ? t('creatives.livePreview')
                    : t('creatives.previewBlankPage')}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="blank">{t('creatives.previewBlankPage')}</SelectItem>
                <SelectItem value="live">{t('creatives.livePreview')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}
        {!showLive && (
          <div className="flex flex-col gap-1">
            <span className="text-xs text-(--ad-text-muted)">{t('creatives.previewMode')}</span>
            <Tabs value={device} onValueChange={(v) => changeDevice(v as PreviewDevice)}>
              <TabsList aria-label={t('creatives.previewMode')} className="h-9">
                {DEVICES.map(({ value, icon: Icon, labelKey }) => (
                  <Tooltip key={value}>
                    {/* A trigger of its own, since the tooltip's data-state would hide the active tab */}
                    <TooltipTrigger asChild>
                      <span className="inline-flex h-full">
                        <TabsTrigger value={value} aria-label={t(labelKey)}>
                          <Icon />
                        </TabsTrigger>
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>{t(labelKey)}</TooltipContent>
                  </Tooltip>
                ))}
              </TabsList>
            </Tabs>
          </div>
        )}
        {name && (
          <p className="min-w-0 flex-1 truncate pb-2 text-sm text-(--ad-text-muted)">{name}</p>
        )}
        <div className="ml-auto flex items-center gap-1 pb-0.5">
          {!showLive && (
            <ToolbarButton label={t('creatives.previewReload')} onClick={reload}>
              <RotateCw />
            </ToolbarButton>
          )}
          <ToolbarButton
            label={
              fullScreen ? t('creatives.previewExitFullScreen') : t('creatives.previewFullScreen')
            }
            onClick={toggleFullScreen}
          >
            {fullScreen ? <Minimize /> : <Maximize />}
          </ToolbarButton>
        </div>
      </header>

      <main
        className={cn(
          'flex min-h-0 flex-1 overflow-auto p-4',
          (device === 'mobile' || showLive) && 'items-start justify-center'
        )}
      >
        {showLive ? (
          <Card className="w-full max-w-2xl">
            <CardHeader>
              <CardTitle>{t('creatives.livePreview')}</CardTitle>
            </CardHeader>
            <CardContent>
              <CreativeLivePreviewForm creative={creative} enabled />
            </CardContent>
          </Card>
        ) : (
          <iframe
            key={`${device}-${loadCount}`}
            ref={frame}
            src={frameSrc}
            title={t('creatives.previewBlankPageTitle')}
            className={cn(
              'shrink-0 bg-card',
              device === 'desktop'
                ? 'size-full rounded-xl border shadow-sm'
                : 'rounded-4xl border-8 border-(--ad-text) shadow-lg'
            )}
            style={
              device === 'mobile'
                ? // The border sits outside the emulated viewport, as a phone's bezel does
                  {
                    width: MOBILE_VIEWPORT.width + 16,
                    height: MOBILE_VIEWPORT.height + 16,
                    boxSizing: 'border-box',
                  }
                : undefined
            }
          />
        )}
      </main>

      {!showLive && (
        // Tucked away at the bottom until asked for, so the creative gets the room
        <Collapsible
          open={panelOpen}
          onOpenChange={setPanelOpen}
          className="shrink-0 border-t bg-card"
        >
          <CollapsibleTrigger
            className="flex w-full items-center gap-2 px-4 py-2 text-left text-sm font-medium hover:bg-(--ad-neutral-soft)"
            aria-label={
              panelOpen ? t('creatives.previewHideEvents') : t('creatives.previewShowEvents')
            }
          >
            {t('creatives.previewEvents')}
            {events.length > 0 && <Badge variant="secondary">{events.length}</Badge>}
            <ChevronUp
              className={cn('ml-auto size-4 transition-transform', panelOpen && 'rotate-180')}
            />
          </CollapsibleTrigger>
          <CollapsibleContent className="h-80 bg-background p-4">
            <CreativePreviewEventPanel events={events} />
          </CollapsibleContent>
        </Collapsible>
      )}
    </div>
  )
}
